<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useMessageContext } from '../provider';

defineProps({
  inboxName: { type: String, default: '' },
});

const SEPARATOR = '·';
const { t } = useI18n();
const { contentAttributes, createdAt, sender } = useMessageContext();
const call = computed(() => contentAttributes.value.gowaCall);
const hasAnswer = computed(
  () =>
    !!call.value.signals?.accept ||
    call.value.signals?.terminate?.reason === 'accepted_elsewhere' ||
    ['CONNECTED', 'ACCEPTEDELSEWHERE'].includes(call.value.signals?.log?.result)
);
const callStates = computed(() => ({
  attempt: {
    label: t('GOWA_CALLS.ATTEMPT'),
    icon: 'i-lucide-phone-incoming',
    color: 'bg-n-amber-3 text-n-amber-11',
  },
  answered: {
    label: t('GOWA_CALLS.ANSWERED'),
    icon: 'i-lucide-phone',
    color: 'bg-n-teal-3 text-n-teal-11',
  },
  rejected: {
    label: t('GOWA_CALLS.REJECTED'),
    icon: 'i-lucide-phone-off',
    color: 'bg-n-ruby-3 text-n-ruby-11',
  },
  missed: {
    label: t('GOWA_CALLS.MISSED'),
    icon: 'i-lucide-phone-missed',
    color: 'bg-n-ruby-3 text-n-ruby-11',
  },
  cancelled: {
    label: t('GOWA_CALLS.CANCELLED'),
    icon: 'i-lucide-phone-off',
    color: 'bg-n-amber-3 text-n-amber-11',
  },
  failed: {
    label: t('GOWA_CALLS.FAILED'),
    icon: 'i-lucide-phone-off',
    color: 'bg-n-amber-3 text-n-amber-11',
  },
  unavailable: {
    label: t('GOWA_CALLS.UNAVAILABLE'),
    icon: 'i-lucide-phone-off',
    color: 'bg-n-amber-3 text-n-amber-11',
  },
  ended: {
    label: hasAnswer.value ? t('GOWA_CALLS.ENDED') : t('GOWA_CALLS.MISSED'),
    icon: 'i-lucide-phone-off',
    color: 'bg-n-slate-3 text-n-slate-11',
  },
}));
const state = computed(
  () => callStates.value[call.value.status] || callStates.value.attempt
);
const caller = computed(
  () =>
    call.value.callerName ||
    sender.value?.name ||
    t('GOWA_CALLS.UNKNOWN_CALLER')
);
const occurredAt = computed(() => {
  const timestamp = Date.parse(call.value.occurredAt) / 1000;
  return Number.isFinite(timestamp) ? timestamp : createdAt.value;
});
const readableTime = computed(() =>
  messageTimestamp(occurredAt.value, 'dd/MM · HH:mm')
);
const fullTime = computed(() =>
  messageTimestamp(occurredAt.value, 'dd/MM/yyyy HH:mm:ss')
);
</script>

<template>
  <div
    data-bubble-name="gowa-call"
    class="flex items-center gap-3 max-w-full min-w-0 rounded-xl border border-n-weak bg-n-slate-2 px-3 py-2.5 text-n-slate-12"
  >
    <span
      class="flex items-center justify-center size-8 shrink-0 rounded-full"
      :class="state.color"
      aria-hidden="true"
    >
      <span class="size-4" :class="state.icon" />
    </span>
    <div class="min-w-0 flex flex-col gap-0.5">
      <div class="flex items-baseline flex-wrap gap-x-4 gap-y-0.5">
        <span class="text-sm font-medium">{{ state.label }}</span>
        <time
          :datetime="call.occurredAt"
          :title="fullTime"
          class="text-xs text-n-slate-11 whitespace-nowrap"
        >
          {{ readableTime }}
        </time>
      </div>
      <div class="text-xs break-words">
        {{ caller }}
        <span v-if="call.callerPhone" class="whitespace-nowrap">
          {{ SEPARATOR }} {{ call.callerPhone }}
        </span>
      </div>
      <div
        class="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-n-slate-11"
      >
        <span v-if="inboxName" class="break-words">{{ inboxName }}</span>
        <span v-if="inboxName" aria-hidden="true">{{ SEPARATOR }}</span>
        <span class="inline-flex items-center gap-1 whitespace-nowrap">
          <span class="i-lucide-lock-keyhole size-3" aria-hidden="true" />
          {{ t('GOWA_CALLS.INTERNAL_RECORD') }}
        </span>
      </div>
    </div>
  </div>
</template>
